import { CreateLabel } from '@/components/CreateLabel';
import { CustomSelectItem } from '@/components/CustomSelect';
import { IconMinus } from '@/components/icons/IconMinus';
import { CustomAutoComplete } from '@/components/ui/Autocomplete';
import { Input } from '@/components/ui/Input';
import { billAmountContent } from '@/content/billAmountContent';
import { BillAmountProvider, ProviderDropdownItem } from '@/state/state.types';
import { ActionIcon, Box, CloseButton, Flex, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { memo, useCallback, useState } from 'react';
import { BillAmountRow, mapDropdownToProvider } from './hooks/useBillAmountForm';
import { useProviderDropdown } from './hooks/useProviderDropdown';

type BillAmountRowItemProps = {
  row: BillAmountRow;
  onAmountChange: (key: string, value: string) => void;
  onAmountBlur: (key: string) => void;
  onProviderSelect: (key: string, provider: BillAmountProvider) => void;
  onProviderClear: (key: string) => void;
  onCustomProviderClick: (key: string, initialValues?: BillAmountProvider) => void;
  onProviderBlur: (key: string) => void;
  onRemove: (key: string) => void;
  showRemove: boolean;
  isLoading: boolean;
};

export const BillAmountRowItem = memo(
  ({
    row,
    onAmountChange,
    onAmountBlur,
    onProviderSelect,
    onProviderClear,
    onCustomProviderClick,
    onProviderBlur,
    onRemove,
    showRemove,
    isLoading,
  }: BillAmountRowItemProps) => {
    const [searchTerm, setSearchTerm] = useState(row.providerSearchTerm);
    const [debouncedSearchValue] = useDebouncedValue(searchTerm, 500, { leading: true });

    const { providerDropdown_: dropdownData, isLoading: providerDataLoading } = useProviderDropdown(
      {
        search: debouncedSearchValue,
        value: row.provider?.id,
      },
    );

    const handleAmountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value.replace(/^\$\s*/, '');
        onAmountChange(row.key, raw);
      },
      [onAmountChange, row.key],
    );

    const handleAmountBlur = useCallback(() => {
      onAmountBlur(row.key);
    }, [onAmountBlur, row.key]);

    const handleProviderChange = useCallback(
      (value: string | null) => {
        if (!value) {
          onProviderClear(row.key);
          return;
        }
        const selectedProvider = dropdownData?.find((item) => item.value === value);
        if (selectedProvider) {
          onProviderSelect(row.key, mapDropdownToProvider(selectedProvider));
        }
      },
      [dropdownData, onProviderClear, onProviderSelect, row.key],
    );

    const handleProviderCreate = useCallback(
      (query: string) => {
        onCustomProviderClick(row.key, { name: query, state: '', city: '' });
        return null;
      },
      [onCustomProviderClick, row.key],
    );

    const handleProviderBlur = useCallback(() => {
      onProviderBlur(row.key);
    }, [onProviderBlur, row.key]);

    const handleRemove = useCallback(() => {
      onRemove(row.key);
    }, [onRemove, row.key]);

    const handleProviderFocus = useCallback(() => {
      if (row.isCustomProvider && row.provider) {
        onCustomProviderClick(row.key, row.provider);
      }
    }, [row.isCustomProvider, row.provider, onCustomProviderClick, row.key]);

    const handleClearCustomProvider = useCallback(() => {
      onProviderClear(row.key);
    }, [onProviderClear, row.key]);

    const amountValue = row.amountDisplay ? `$ ${row.amountDisplay}` : '';

    return (
      <Flex
        gap="sm"
        align="center"
      >
        <Flex
          gap="md"
          align="flex-start"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Box sx={{ flex: 1 }}>
            <Text
              fw={700}
              c="primary.0"
              size="sm"
              style={{ textAlign: 'center' }}
              mb={8}
            >
              {billAmountContent.amountLabel}
            </Text>
            <Input
              type="text"
              name="billAmount"
              placeholder={billAmountContent.amountPlaceholder}
              isLoading={isLoading}
              error={row.errors.amount}
              inputProps={{
                value: amountValue,
                onChange: handleAmountChange,
                onBlur: handleAmountBlur,
                mt: 0,
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Text
              fw={700}
              c="primary.0"
              size="sm"
              mb={4}
              style={{ textAlign: 'center' }}
            >
              {billAmountContent.providerLabel}
            </Text>
            {row.isCustomProvider && row.provider ? (
              <Input
                type="text"
                name="provider"
                placeholder={billAmountContent.providerPlaceholder}
                isLoading={isLoading}
                error={row.errors.provider}
                inputProps={{
                  value: row.provider.name,
                  readOnly: true,
                  onClick: handleProviderFocus,
                  mt: { base: 0, md: 8 },
                  rightSection: (
                    <CloseButton
                      size="sm"
                      onClick={handleClearCustomProvider}
                    />
                  ),
                  sx: { cursor: 'pointer' },
                }}
              />
            ) : (
              <CustomAutoComplete
                placeholder={billAmountContent.providerPlaceholder}
                searchable
                creatable
                isLoading={providerDataLoading}
                data={(dropdownData ?? []) as ProviderDropdownItem[]}
                dropdownComponent="div"
                itemComponent={CustomSelectItem}
                getCreateLabel={(query) => <CreateLabel label={query} />}
                rightSection={<></>}
                clearable
                onCreate={handleProviderCreate}
                onSearchChange={setSearchTerm}
                searchValue={searchTerm}
                disabled={isLoading}
                value={row.provider?.id ?? null}
                onChange={handleProviderChange}
                onBlur={handleProviderBlur}
                error={row.errors.provider}
                mt={{ base: 0, md: 8 }}
                label=""
              />
            )}
          </Box>
        </Flex>

        <Box
          w={36}
          mt={30}
          sx={{ flexShrink: 0 }}
        >
          {showRemove && (
            <ActionIcon
              variant="outline"
              color="red"
              radius="xl"
              size={36}
              onClick={handleRemove}
              styles={{
                root: {
                  borderColor: '#E53E3E',
                  borderWidth: 2,
                  flexShrink: 0,
                },
              }}
            >
              <IconMinus />
            </ActionIcon>
          )}
        </Box>
      </Flex>
    );
  },
);

BillAmountRowItem.displayName = 'BillAmountRowItem';
